/**
 * PC-v2.2-C2.1 — «SEGUIMIENTO DEL SERVICIO» EN LA CALIDAD INTERNA
 *
 * Los gráficos G1–G4 y G6 del portal (`portal-evolucion.tsx`, el mismo `Contenido`) con el MISMO cálculo
 * (`GET /api/calidad/evolucion`), sobre los clientes y centros a los que el usuario tiene acceso. Es la perspectiva
 * del cliente: sólo cuenta lo que el cliente ve. NO sustituye a los indicadores internos de la pestaña (que cuentan
 * todo, también lo interno, y fechan la resolución por su primer evento): por eso va en su propio bloque, con nombre
 * propio, plegado y debajo de ellos.
 *
 * La configuración del portal del cliente (GENERAL → CLIENTE → CENTRO) no se aplica aquí: qué gráficos se ven lo
 * deciden sólo los permisos del usuario (los mismos que exige el servidor para darle esas cifras).
 */
import { useId, type ReactNode } from 'react';

import { api, type EvolucionInterna } from '../api.ts';
import { Cargando, Error as ErrorCaja, useDatos } from '../componentes/base.tsx';
import { BotonExportarExcel } from '../componentes/exportar-excel.tsx';
import { IND } from '../componentes/indicadores-portal.ts';
import { useSesion } from '../contexto.tsx';
import type { Rango } from '../periodo-analitico.ts';
import { Contenido, IDS_EVOLUCION, type DatosG6 } from './portal-evolucion.tsx';

/** Los gráficos que el usuario puede ver: con `ots:leer`, los de OT; con `incidencias:leer`, el de incidencias y G6. */
function idsPermitidos(puede: (p: string) => boolean, conCentro: boolean): Set<string> {
  return new Set(IDS_EVOLUCION.filter((id) => {
    if (id === IND.calidad.evolCumplimiento || id === IND.calidad.evolOt) return puede('ots:leer');
    if (id === IND.calidad.evolIncidencias) return puede('incidencias:leer');
    if (id === IND.calidad.evolPorCentro) return puede('incidencias:leer') && !conCentro;
    return true;
  }));
}

export function BloqueSeguimiento({ rango, error, idCliente, idCentro, agrupar, abierto, alCambiar, fijarAgrupar }: {
  rango: Rango;
  /** Por qué el periodo no se puede pedir (más de 366 días, fechas inválidas), o `null`. */
  error: string | null;
  idCliente: string;
  idCentro: string;
  agrupar: string;
  abierto: boolean;
  alCambiar: (abierto: boolean) => void;
  fijarAgrupar: (v: string) => void;
}): ReactNode {
  const { puede } = useSesion();
  if (!puede('nc:leer')) return null;
  const ids = idsPermitidos(puede, idCentro !== '');
  return (
    <details className="panel portal-evolucion" open={abierto} onToggle={(e) => { if (e.currentTarget.open !== abierto) alCambiar(e.currentTarget.open); }}>
      <summary className="panel-cabecera">
        <h2 style={{ margin: 0 }}>Seguimiento del servicio</h2>
        <span className="pequena tenue">lo que ve el cliente · {ids.size} gráfico(s)</span>
      </summary>
      {abierto && (
        <div className="panel-cuerpo">
          <p className="pequena tenue" style={{ marginTop: 0 }}>
            Las mismas cifras que el cliente ve en su portal: órdenes por fecha prevista con su situación de hoy; incidencias
            visibles para el cliente por fecha de registro y de resolución; no conformidades visibles o abiertas por el
            cliente, por fecha de apertura y de cierre. No sustituyen a los indicadores internos de arriba, que cuentan
            también lo interno.
          </p>
          {error !== null
            ? <p className="pequena" role="status">No se puede mostrar con este periodo: {error}</p>
            : (
              <>
                {/* PC-v2.2-C2.2: el Excel de estos gráficos (perspectiva «Seguimiento del servicio»), con los mismos filtros. */}
                <BotonExportarExcel ruta={`/api/calidad/evolucion/exportar?${consultaDe(rango, idCliente, idCentro)}`} />
                <Graficos rango={rango} idCliente={idCliente} idCentro={idCentro} ids={ids} agrupar={agrupar} fijarAgrupar={fijarAgrupar} />
              </>
            )}
        </div>
      )}
    </details>
  );
}

function Graficos({ rango, idCliente, idCentro, ids, agrupar, fijarAgrupar }: {
  rango: Rango; idCliente: string; idCentro: string; ids: ReadonlySet<string>; agrupar: string; fijarAgrupar: (v: string) => void;
}): ReactNode {
  const idBase = useId();
  const q = consultaDe(rango, idCliente, idCentro);
  const d = useDatos(() => api.get<EvolucionInterna>(`/api/calidad/evolucion?${q}`), [q]);
  //  Nunca la serie de otro cliente, centro o periodo mientras llega la nueva.
  const e = d.datos !== null && !d.cargando && d.datos.periodo.desde === rango.desde && d.datos.periodo.hasta === rango.hasta
    && (d.datos.idCentro ?? '') === idCentro && (idCliente === '' || d.datos.idCliente === idCliente) ? d.datos : null;
  const porCliente = e?.porCliente ?? null;
  const g6 = e === null ? null : datosG6(e, porCliente !== null && agrupar !== 'centro' ? 'cliente' : 'centro', idCliente === '');
  return (
    <>
      {d.error !== null && <ErrorCaja error={d.error} reintentar={d.recargar} />}
      {e === null && d.error === null && <Cargando texto="Calculando el seguimiento del servicio…" />}
      {e !== null && (
        <Contenido
          e={e} ids={ids} perspectiva="interna" g6={g6}
          selectorG6={porCliente === null ? undefined : (
            <div className="campo" style={{ margin: '0 0 8px', maxWidth: 220 }}>
              <label htmlFor={`${idBase}-g6`}>Agrupar por</label>
              <select id={`${idBase}-g6`} value={g6?.columna === 'Cliente' ? 'cliente' : 'centro'} onChange={(ev) => fijarAgrupar(ev.target.value)}>
                <option value="cliente">Cliente</option>
                <option value="centro">Centro</option>
              </select>
            </div>
          )}
        />
      )}
    </>
  );
}

/** Los filtros de la vista, iguales para los gráficos y para su Excel. */
function consultaDe(rango: Rango, idCliente: string, idCentro: string): string {
  const q = new URLSearchParams({ desde: rango.desde, hasta: rango.hasta });
  if (idCliente !== '') q.set('idCliente', idCliente);
  if (idCentro !== '') q.set('idCentro', idCentro);
  return q.toString();
}

/** G6 por cliente o por centro. Sin un cliente elegido, cada centro lleva su cliente: dos «Hospital Norte» son dos filas. */
function datosG6(e: EvolucionInterna, por: 'cliente' | 'centro', conCliente: boolean): DatosG6 | null {
  if (por === 'cliente' && e.porCliente !== null) {
    return {
      columna: 'Cliente',
      filas: e.porCliente.filas.map((c) => ({ clave: c.idCliente, nombre: `${c.cliente} (${String(c.centros)} centro${c.centros === 1 ? '' : 's'})`, incidencias: c.incidencias, nc: c.nc })),
      resto: e.porCliente.resto,
    };
  }
  if (e.porCentro === null) return null;
  return {
    columna: 'Centro',
    filas: e.porCentro.filas.map((c) => ({ clave: c.idCentro, nombre: conCliente ? `${c.centro} · ${c.cliente}` : c.centro, incidencias: c.incidencias, nc: c.nc })),
    resto: e.porCentro.resto,
  };
}
